import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import {
  LabelAction,
  ModalAction,
  ModalContent,
  ModalImportFileConfig,
} from '../../types/fields.type';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SnackBarService } from '@hospitality-bot/shared/material';
import { downloadFile } from '../../utils/shared';
import { Option } from '../../types/form.type';

@Component({
  selector: 'hospitality-bot-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent {
  customTemplate!: TemplateRef<any>;
  templateContext: any;
  heading: string = 'Notification';
  icon?: string;
  iconClass?: string;

  descriptions: string[] = ['Are you sure?'];
  htmlDescription!: string;
  title!: string;
  infoNote?:
    | {
        type?: 'info' | 'warning' | 'danger';
        text?: string;
        icon?: string;
        template?: TemplateRef<any>;
        customClass?: string;
        customStyle?: Record<string, any>;
      }
    | string;

  get infoNoteType(): 'info' | 'warning' | 'danger' {
    return (typeof this.infoNote === 'object' && this.infoNote?.type) || 'warning';
  }

  get infoNoteText(): string {
    return typeof this.infoNote === 'string'
      ? this.infoNote
      : this.infoNote?.text || '';
  }

  get infoNoteIcon(): string | undefined {
    return typeof this.infoNote === 'object' ? this.infoNote?.icon : undefined;
  }

  get infoNoteTemplate(): TemplateRef<any> | undefined {
    return typeof this.infoNote === 'object' ? this.infoNote?.template : undefined;
  }

  get infoNoteClass(): string {
    return (typeof this.infoNote === 'object' && this.infoNote?.customClass) || '';
  }

  get infoNoteStyle(): Record<string, any> | undefined {
    return typeof this.infoNote === 'object' ? this.infoNote?.customStyle : undefined;
  }
  isRemarks: boolean = false;
  isReason: boolean = false;
  isReservation: boolean = false;
  isLateCheckout: boolean = false;
  journeyDetails?: any;
  departureDate?: number;
  folioOptions: Option<any>[] = [];
  defaultFolioNumber?: number;
  timeRangeErrorMessage: string = '';
  isDate: boolean = false;
  toDate!: string;
  fromDate!: string;
  showActionsCTA = true;
  isCsvUpload = false;
  remarksValidators: Validators[] = [Validators.required];
  uploadCsvConfig!: ModalImportFileConfig;
  selectedFile: File;
  @Input() reasonValidators: Validators[] = [];

  @Input() showDescriptionBlock = true;
  @Input() isDefaultRefClose = true;
  @Output() onClose = new EventEmitter();

  constructor(
    private fb: FormBuilder,
    public dialogConfig: DynamicDialogConfig, //generic not supported yet,
    public dialogRef: DynamicDialogRef,
    private snackbarService: SnackBarService
  ) {
    const data = dialogConfig?.data as ModalContent;
    if (data) {
      this.mapInputData(data);
    }
  }

  modelForm!: FormGroup;

  checkoutTimeOptions: Option<number>[] = [];

  ngOnInit(): void {
    this.modelForm = this.fb.group({
      reason: ['', this.isReason ? this.reasonValidators : []],
      remarks: ['', this.isReason ? [] : this.remarksValidators],
    });

    if (this.isReservation) {
      // Add controls conditionally when isReservation is true
      this.modelForm.addControl('chargeable', this.fb.control(false));
      this.modelForm.addControl('inclusiveTax', this.fb.control(false));
      this.modelForm.addControl('chargedAmount', this.fb.control(0));
      this.modelForm.get('chargeable')?.valueChanges.subscribe((res) => {
        if (res === false) {
          this.modelForm.get('chargedAmount')?.patchValue(0);
        } else if (this.journeyDetails?.payableAmount) {
          this.modelForm
            .get('chargedAmount')
            ?.patchValue(this.journeyDetails.payableAmount);
        }
      });
    }

    if (this.isLateCheckout) {
      const startTime = this.journeyDetails?.journeyStartTime || '12:00:00';
      const endTime = this.journeyDetails?.journeyEndTime || '23:59:59';
      this.checkoutTimeOptions = this.getTimeOptions(startTime, endTime);

      const defaultTime =
        this.checkoutTimeOptions.length > 0
          ? this.checkoutTimeOptions[0].value
          : 0;

      this.modelForm.addControl(
        'extendedTime',
        this.fb.control(defaultTime, [Validators.required])
      );

      const defaultFolio =
        this.defaultFolioNumber ??
        (this.folioOptions?.length ? this.folioOptions[0].value : 1);

      this.modelForm.addControl(
        'folioNumber',
        this.fb.control(defaultFolio, [Validators.required])
      );
    }
  }

  defaultAction: ModalAction = {
    label: 'Ok',
    onClick: () => {
      this.close();
    },
    variant: 'contained',
    type: 'SUCCESS',
  };

  @Input() actions!: ModalAction[];

  @Input() set content(value: ModalContent) {
    this.mapInputData(value);
  }

  mapInputData(value: ModalContent) {
    Object.entries(value).forEach(([key, val]) => {
      this[key] = val;
    });
  }

  close() {
    this.isDefaultRefClose && this.dialogRef.close();
    this.onClose.emit();
  }

  onButtonClick(value: ModalAction) {
    if (
      value.type === 'SUCCESS' &&
      this.modelForm.invalid &&
      (this.isRemarks ||
        this.isReason ||
        this.isReservation ||
        this.isLateCheckout)
    ) {
      this.modelForm.markAllAsTouched();
    } else {
      const formValue = { ...this.modelForm.value };
      if (this.isReservation && !formValue.chargeable) {
        formValue.chargedAmount = 0;
      }
      if (this.isLateCheckout) {
        let extMinutes =
          formValue.extendedTime ??
          formValue.extendedTIme ??
          formValue.updatedCheckoutTime ??
          0;
        if (typeof extMinutes === 'string' && extMinutes.includes(':')) {
          const [hours, minutes] = extMinutes.split(':').map(Number);
          const startSec = this.parseTimeToSeconds(
            this.journeyDetails?.journeyStartTime || '12:00:00'
          );
          const startMinutes =
            startSec !== -1 ? Math.floor(startSec / 60) : 720;
          const selectedMinutes = (hours || 0) * 60 + (minutes || 0);
          extMinutes = Math.max(0, selectedMinutes - startMinutes);
        } else {
          extMinutes = Number(extMinutes) || 0;
        }

        formValue.extendedTime = extMinutes;
        delete formValue.extendedTIme;
        delete formValue.updatedCheckoutTime;
      }
      value.onClick(formValue);
    }
  }

  formatSize(bytes: number) {
    if (bytes === 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  }

  onChooseFile($event: {
    file: File;
    pageType: string;
    documentType: string;
    binaryData: string;
  }) {
    this.selectedFile = $event.file;
  }

  downloadSampleFile() {
    downloadFile(
      this.uploadCsvConfig?.sampleFileUrl,
      this.uploadCsvConfig?.downloadFileName
    );
  }

  onCsvImport(value: ModalImportFileConfig) {
    if (this.selectedFile) {
      value.onClick(this.selectedFile);
    }
  }

  formatToHHMM(timeStr: string): string {
    if (!timeStr) return '12:00';
    const parts = String(timeStr).split(':');
    const h = (parts[0] || '00').padStart(2, '0');
    const m = (parts[1] || '00').padStart(2, '0');
    return `${h}:${m}`;
  }

  parseTimeToSeconds(timeStr: string | number): number {
    if (timeStr == null || timeStr === '') return -1;
    if (typeof timeStr === 'number') {
      if (timeStr < 86400) return timeStr;
      const d = new Date(timeStr < 10000000000 ? timeStr * 1000 : timeStr);
      return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
    }
    const parts = String(timeStr)
      .split(':')
      .map((p) => parseInt(p, 10));
    if (parts.some((p) => isNaN(p))) return -1;
    const h = parts[0] || 0;
    const m = parts[1] || 0;
    const s = parts[2] || 0;
    return h * 3600 + m * 60 + s;
  }

  isTimeWithinRange(
    timeStr: string,
    startTime: string,
    endTime: string
  ): boolean {
    const valSec = this.parseTimeToSeconds(timeStr);
    const startSec = this.parseTimeToSeconds(startTime);
    const endSec = this.parseTimeToSeconds(endTime);
    if (valSec === -1) return false;
    if (startSec !== -1 && valSec < startSec) return false;
    if (endSec !== -1 && valSec > endSec) return false;
    return true;
  }

  isTodayDate(timestamp: number): boolean {
    const ts = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    const d = new Date(ts);
    const today = new Date();
    return (
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear()
    );
  }

  getTimeOptions(startTimeStr: string, endTimeStr: string): Option<number>[] {
    const startSec = this.parseTimeToSeconds(startTimeStr);
    const endSec = this.parseTimeToSeconds(endTimeStr);

    const startMinFromJourney =
      startSec !== -1 ? Math.floor(startSec / 60) : 12 * 60;
    const endMinFromJourney =
      endSec !== -1 ? Math.floor(endSec / 60) : 23 * 60 + 59;

    const options: Option<number>[] = [];

    for (let m = startMinFromJourney + 30; m <= endMinFromJourney; m += 30) {
      const hh = Math.floor(m / 60);
      const mm = m % 60;
      const mmStr = String(mm).padStart(2, '0');

      const period = hh >= 12 ? 'PM' : 'AM';
      const displayHour = hh % 12 === 0 ? 12 : hh % 12;
      const displayHourStr = String(displayHour).padStart(2, '0');

      const diffMinutes = m - startMinFromJourney;
      let durationText = '';
      if (diffMinutes > 0) {
        const diffHours = diffMinutes / 60;
        durationText =
          diffHours === 1
            ? ` (+1 hr)`
            : diffHours % 1 === 0
            ? ` (+${diffHours} hrs)`
            : ` (+${diffHours.toFixed(1)} hrs)`;
      }

      const label = `${displayHourStr}:${mmStr} ${period}${durationText}`;

      options.push({
        label: label,
        value: diffMinutes,
      });
    }

    const endH = Math.floor(endMinFromJourney / 60);
    const endM = endMinFromJourney % 60;
    const diffMinutesEnd = endMinFromJourney - startMinFromJourney;
    if (
      diffMinutesEnd > 0 &&
      !options.some((o) => o.value === diffMinutesEnd)
    ) {
      const period = endH >= 12 ? 'PM' : 'AM';
      const displayHour = endH % 12 === 0 ? 12 : endH % 12;
      let durationText = '';
      if (diffMinutesEnd > 0) {
        const diffHours = diffMinutesEnd / 60;
        durationText =
          diffHours === 1
            ? ` (+1 hr)`
            : diffHours % 1 === 0
            ? ` (+${diffHours} hrs)`
            : ` (+${diffHours.toFixed(1)} hrs)`;
      }
      const label = `${String(displayHour).padStart(2, '0')}:${String(
        endM
      ).padStart(2, '0')} ${period}${durationText}`;
      options.push({ label, value: diffMinutesEnd });
    }

    return options;
  }
}
